import React, { useEffect, useRef, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdDeleteForever, MdVisibility } from "react-icons/md";
import { Link } from "react-router-dom";
import { useSelector } from 'react-redux';

import { IconButton } from "@material-ui/core";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Form } from "@unform/web";
import * as Yup from "yup";
import DivDiagonal from "../../components/DivDiagonal";
import CustomButton from "../../components/Form/Button";
import InputDate from "../../components/Form/InputDate";
import ViewSaleModal from "./ViewSaleModal";
import DeleteSaleModal from "./DeleteSaleModal";
import { getSales, deleteSale } from "../../services/Sale";
import { toast } from "react-toastify";
import messageTypes from "../../constants/MessagesTypes";
import {
  ProductWrapper,
  PageHeader,
  NewProductButton,
  ProductCard,
  FilterSection,
  FilterTitle,
  FormWrapper,
  StyledFilterFormWrapper,
  ContentArea,
  StyledTableContainer,
  ActionButtons,
  PaginationInfo,
  PaginationButton,
  LoadingButton,
  EmptyState,
  TableWrapper,
  FilterRow,
  FilterInputWrapper,
  FilterButtonWrapper
} from "./styles";

// Função auxiliar para formatar valores monetários corretamente
const formatCurrencyValue = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Função para formatar data
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export default function SalesList() {
  const { currentCompany } = useSelector((state) => state.CompanyReducer);
  const [loading, setLoading] = useState(false);
  const [deleteSaleId, setDeleteSaleId] = useState(null);
  const [viewSale, setViewSale] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [total, setTotal] = useState(0);
  const [sales, setSales] = useState([]);
  const [page, setPage] = useState(1);

  const formRef = useRef(null);

  // Função para obter data de hoje no formato DD/MM/YYYY
  const getTodayDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const [filter, setFilter] = useState({
    startDate: getTodayDate(),
    endDate: null,
  });

  useEffect(() => {
    setLoading(true);

    getSales({
      page: page,
      per_page: 25,
      startDate: filter.startDate,
      endDate: filter.endDate,
    }).then((response) => {
      if (!response || !response.data) {
        return;
      }

      setTotal(response.data.total || 0);
      setSales(response.data.data || []);
      setLoading(false);
    });

    return function cleanup() {
      setSales([]);
    };
  }, [page, deleteSaleId, filter, currentCompany?.id]);

  async function handleSubmit(data) {
    try {
      formRef.current.setErrors({});
      const schema = Yup.object().shape({});

      await schema.validate(data, {
        abortEarly: false,
      });

      setFilter({
        startDate: data.startDate,
        endDate: data.endDate,
      });
      setPage(1);

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
      }
    }
  }

  function handleDelete(id) {
    setDeleteSaleId(id);
    setShowDeleteModal(true);
  }

  async function handleConfirmDelete(restoreStock = true) {
    try {
      await deleteSale(deleteSaleId, restoreStock);
      toast.success(messageTypes.SUCCESS || 'Venda cancelada com sucesso!');
      setDeleteSaleId(null);
      setShowDeleteModal(false);
      // Recarregar a lista
      const response = await getSales({
        page: page,
        per_page: 25,
        startDate: filter.startDate,
        endDate: filter.endDate,
      });
      if (response && response.data) {
        setTotal(response.data.total || 0);
        setSales(response.data.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao cancelar venda');
      setDeleteSaleId(null);
      setShowDeleteModal(false);
    }
  }

  function handleViewSale(sale) {
    setViewSale(sale);
  }

  const totalPages = Math.ceil(total / 25);

  return (
    <ProductWrapper>
      <DivDiagonal />
      <ContentArea className="mt-4">
        <PageHeader>
          <h1>
            <i className="fas fa-shopping-cart"/> Vendas Realizadas <span className="badge">{total}</span>
          </h1>
          <NewProductButton as={Link} to="/sales">
            <i className="fas fa-cash-register" />
            Nova Venda
          </NewProductButton>
        </PageHeader>
        <ProductCard>
          <FilterSection>
            <StyledFilterFormWrapper>
              <Form ref={formRef} onSubmit={handleSubmit}>
                <FilterTitle>
                  <i className="fas fa-funnel-dollar" /> Filtro
                </FilterTitle>
                <FilterRow>
                  <FilterInputWrapper>
                    <InputDate
                      type="date"
                      name="startDate"
                      label={'Data inicial'}
                      defaultValue={filter.startDate}
                    />
                  </FilterInputWrapper>
                  <FilterInputWrapper>
                    <InputDate
                      type="date"
                      name="endDate"
                      label={'Data final'}
                      defaultValue={filter.endDate}
                    />
                  </FilterInputWrapper>
                  <FilterButtonWrapper>
                    <CustomButton
                      className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                      type="submit"
                      startIcon={<AiOutlineSearch/>}
                      style={{
                        background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                        border: 'none',
                        color: '#ffffff',
                        padding: '0 32px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                        transition: 'all 0.3s ease',
                        width: 'auto',
                        minWidth: 'auto',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                      }}
                    >
                      Buscar
                    </CustomButton>
                  </FilterButtonWrapper>
                </FilterRow>
              </Form>
            </StyledFilterFormWrapper>
          </FilterSection>
          <TableWrapper>
          {loading ? (
            <LoadingButton
              className="btn btn-primary text-capitalize font-weight-bold"
              loading={loading}
              type="button"
            >
              Carregando...
            </LoadingButton>
          ) : (
            <>
              {sales && sales.length ? (
                <StyledTableContainer>
                  <Table sx={{minWidth: 650}} aria-label="caption table">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Data</TableCell>
                        <TableCell align="center">Itens</TableCell>
                        <TableCell align="center">Subtotal</TableCell>
                        <TableCell align="center">Desconto</TableCell>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">Opções</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sales.map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell align="center">
                            {formatDate(sale.saleDate)}
                          </TableCell>
                          <TableCell align="center">
                            {sale.items ? sale.items.length : 0}
                          </TableCell>
                          <TableCell align="center">
                            {formatCurrencyValue(sale.subtotal)}
                          </TableCell>
                          <TableCell align="center">
                            {formatCurrencyValue(sale.discount || 0)}
                          </TableCell>
                          <TableCell align="center">
                            <strong style={{ color: '#f29205' }}>
                              {formatCurrencyValue(sale.total)}
                            </strong>
                          </TableCell>
                          <TableCell align="center">
                            <ActionButtons>
                              <IconButton
                                aria-label="view"
                                onClick={() => handleViewSale(sale)}
                              >
                                <MdVisibility />
                              </IconButton>
                              <IconButton
                                aria-label="delete"
                                onClick={() => handleDelete(sale.id)}
                              >
                                <MdDeleteForever />
                              </IconButton>
                            </ActionButtons>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={6}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <PaginationInfo>
                              Mostrando {((page - 1) * 25) + 1} até {Math.min(page * 25, total)} de {total} vendas
                            </PaginationInfo>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <PaginationButton
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                              >
                                <IoIosArrowBack />
                              </PaginationButton>
                              <PaginationButton
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages}
                              >
                                <IoIosArrowForward />
                              </PaginationButton>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </StyledTableContainer>
              ) : (
                <EmptyState>
                  <i className="fas fa-receipt" />
                  <p>Nenhuma venda encontrada</p>
                  <span>Realize vendas para visualizá-las aqui</span>
                </EmptyState>
              )}
            </>
          )}
          </TableWrapper>
        </ProductCard>
      </ContentArea>

      <ViewSaleModal
        sale={viewSale}
        isOpen={!!viewSale}
        onClose={() => setViewSale(null)}
      />

      <DeleteSaleModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteSaleId(null);
        }}
        onConfirm={handleConfirmDelete}
        saleId={deleteSaleId}
      />
    </ProductWrapper>
  );
}

